import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Models } from '@ibexfusion/models';
import { DynamicContentEntranceService } from '@services/dynamic-content/dynamic-content-entrance.service';
import { MemoryService } from '@services/memory/memory.service';
import { DataTableComponent, DataTableEvent } from '../../../../components/data-table/data-table.component';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@services/snackbar/snackbar.service';
import { DialogService } from '@services/dialog/dialog.service';
import { CompositionWizardCustomerSelectComponent } from './composition-wizard-customer-select/composition-wizard-customer-select.component';
import { OrganisationService } from '@services/organisations/organisation.service';
import { PackageInstalledAppsService } from '@services/packages/package-installed-apps.service';
import { MessengerService } from '@services/messenger/messenger.service';
import { MyOrganisationService } from '@services/organisations/my-organisation.service';
import { EntranceTypeByType } from './entrance-type.map';
import { Router } from '@angular/router';
import { DynamicContentService } from '@services/dynamic-content/dynamic-content.service';
import { ProductService } from '@services/products/product.service';

@Component({
    selector: 'app-composition-wizard-dynamic-entrance',
    templateUrl: './composition-wizard-dynamic-entrance.component.html',
    styleUrls: ['./composition-wizard-dynamic-entrance.component.scss'],
    providers: [DynamicContentEntranceService, DynamicContentService, OrganisationService, PackageInstalledAppsService, MyOrganisationService, ProductService],
    standalone: false
})
export class CompositionWizardDynamicEntranceComponent implements OnInit {

  @Input() composition: Models.Composition;
  @ViewChild('dynamicContentTable') dynamicContentTable: DataTableComponent;
  @Output() save: EventEmitter<string> = new EventEmitter();

  public assignedContentEntranceService: MemoryService<Models.DynamicContentEntrance>;
  public columns = [
    { prop: '_id' }, { prop: 'title' }, { prop: 'type' }, { prop: 'OwnerOrganisationID' }, { prop: 'OwnerCustomerID' }, { prop: 'children.length', name: 'Children' }
  ];
  private dynamicContentEntrances: Models.DynamicContentEntrance[];

  constructor(
    private dynamicContentEntranceService: DynamicContentEntranceService, private dynamicContentService: DynamicContentService, private organisationService: OrganisationService,
    private packageInstalledAppsService: PackageInstalledAppsService, private productService: ProductService, private messengerService: MessengerService,
    private myOrganisationService: MyOrganisationService, private dialogService: DialogService, private dialog: MatDialog, private router: Router, private snackbar: SnackbarService
  ) {
  }

  ngOnInit() {
    this.dynamicContentEntrances = [];
    this.assignedContentEntranceService = new MemoryService(this.dynamicContentEntrances);
    this.getEntrances();
  }

  public updateOrCreateDynamicContent(entrance = null) {
    this.openConfigurationDialog(entrance).then(result => {
      if (!result || !result.entranceType) {
        return;
      }
      const entranceType = EntranceTypeByType.get(result.entranceType);
      let dynamicContentEntrance = <Models.DynamicContentEntrance>Models.ModelFactory.createModelInstanceByName('DynamicContentEntrance');
      if (entrance) {
        dynamicContentEntrance = entrance
      }
      dynamicContentEntrance.title = entranceType.title;
      dynamicContentEntrance.type = entranceType.type;
      dynamicContentEntrance.readonly = result.readonly;
      dynamicContentEntrance.noCategories = result.noCategories;
      dynamicContentEntrance.config = result.config;
      if (!result.customerId) {
        this.createOrUpdate(dynamicContentEntrance);
        return;
      }
      dynamicContentEntrance.OwnerCustomerID = result.customerId;
      const ownerChange = dynamicContentEntrance.OwnerOrganisationID && dynamicContentEntrance.OwnerOrganisationID !== result.ownerOrganisationID;
      if (result.ownerOrganisationID) {
        dynamicContentEntrance.OwnerOrganisationID = result.ownerOrganisationID;
      }
      const query = { $or: [{ ibex3customerId: result.customerId }, { organisationCustomerId: result.customerId }] };
      this.setOwnerOrganisation(query, dynamicContentEntrance).then(success => { // if not assigned we look for organisation by customerId
        if (success) {
          this.createOrUpdate(dynamicContentEntrance, ownerChange);
        } else {
          return;
        }
      });
    });
  }

  public setOwnerOrganisation(query, dynamicContentEntrance): Promise<any> {
    if (dynamicContentEntrance.OwnerOrganisationID) {
      return Promise.resolve(true);
    }
    return this.organisationService.find(0, 100, query).then(organisations => {
      if (organisations.data.length === 0) {
        this.dialogService.confirm('No Organisation for customer found, do you like to create anyway ?')
          .then(res => {
            return Promise.resolve(res);
          });
      } else {
        if (organisations.data.length > 1) {
          console.log('more then one organisation has this customer assigned, we take first match');
        }
        dynamicContentEntrance.OwnerOrganisationID = organisations.data[0].OrganisationID.toString();
        return Promise.resolve(true);
      }
    });
  }

  public openConfigurationDialog(dynamicContentEntrance): Promise<any> {
    let dialogRef: MatDialogRef<CompositionWizardCustomerSelectComponent>;
    const data = dynamicContentEntrance ? {
      type: dynamicContentEntrance.type,
      id: dynamicContentEntrance.OwnerCustomerID,
      config: dynamicContentEntrance.config,
      readonly: dynamicContentEntrance.readonly,
      noCategories: dynamicContentEntrance.noCategories,
      ownerOrganisationID: dynamicContentEntrance.OwnerOrganisationID
    } : null
    const config: MatDialogConfig = {
      data: data
    };

    dialogRef = this.dialog.open(CompositionWizardCustomerSelectComponent, config);

    return dialogRef.afterClosed().toPromise().then(result => {
      return Promise.resolve(result);
    });
  }

  public createOrUpdate(dynamicContentEntrance, ownerChange = false) {
    if (dynamicContentEntrance._id) { // edit
      this.dynamicContentEntranceService.update(dynamicContentEntrance._id, dynamicContentEntrance).then(result => {
        if (!result) {
          console.log('could not update');
          return;
        }
        this.save.emit();
        this.getEntrances();
        this.snackbar.success('Content updated');
        if (ownerChange) {
          this.updateOwnerOrganisationOnChildren(result.children, result.OwnerOrganisationID);
        }
      });
    } else { // create
      this.dynamicContentEntranceService.create(dynamicContentEntrance).then(result => {
        if (!result) {
          console.log('could not create');
          return;
        }
        if (!this.composition.dynamicContentIds) {
          this.composition.dynamicContentIds = [];
        }
        this.composition.dynamicContentIds.push(result._id);
        this.save.emit();
        this.getEntrances();
        this.snackbar.success('Content created');
      });
    }
  }

  public updateOwnerOrganisationOnChildren(childIds, ownerOrganisationID, skip = 0) {
    if (!childIds || !childIds.length) {
      return;
    } else {
      return this.dynamicContentService.find(skip, 500, { $or: [{ _id: { $in: childIds } }, { masterLanguageEntry: { $in: childIds } }] })
        .then(children => {
          if (!children || !children.data || !children.data.length) {
            return;
          }
          if (children.total - skip > children.data.length) { // if we have more then 500 items
            this.dynamicContentService.find(skip + 500, 500, { $or: [{ _id: { $in: childIds } }, { masterLanguageEntry: { $in: childIds } }] });
          }
          const ancestorIds = [];
          children.data.map(child => {
            if (child.children) {
              ancestorIds.concat(child.children);
            }
            if (child.content && child.content.products) {
              this.productService.patch(child.content.products, { OwnerOrganisationID: ownerOrganisationID });
            }
            this.dynamicContentService.patch(child._id, { OwnerOrganisationID: ownerOrganisationID });
          });
          if (ancestorIds.length) {
            return this.updateOwnerOrganisationOnChildren(ancestorIds, ownerOrganisationID);
          }
        });
    }
  }

  public updateOwnerOrganisationOnProducts() {

  }

  public detail(event: DataTableEvent) {
    this.myOrganisationService.get(event.row.OwnerOrganisationID).then(organisation => {
      if (!organisation) {
        this.snackbar.open('you have no access to this organisation');
        return;
      }
      this.messengerService.setOrganisation(organisation);
      setTimeout(() =>
        this.router.navigate([{ outlets: { org: organisation.name, primary: 'dynamic-content' } }],
          { queryParams: { entranceId: event.row._id } }
        ), 1);
    });
  }

  public edit(event: DataTableEvent) {
    this.updateOrCreateDynamicContent(event.row);
  }

  public remove(event: DataTableEvent) {
    if (event.row.children && event.row.children.length) {
      this.snackbar.error('Failed to remove content: remove first all assigned children');
      return;
    }
    this.packageInstalledAppsService.find(0, 100, { dynamicContentEntranceId: event.row._id }).then(result => {
      if (result.data && result.data.length) {
        this.snackbar.error('Failed to remove content: entrance is still connected to package');
        return;
      }
      this.dialogService
        .confirm('Remove Dynamic Content?')
        .then(res => {
          if (!res) {
            return;
          }
          this.dynamicContentEntranceService.remove(event.row._id).then(() => {
            this.save.emit();
            this.getEntrances();
            this.snackbar.success('Content removed');
          });
        });
    });
  }

  private getEntrances() {
    if (!this.composition.dynamicContentIds || !this.composition.dynamicContentIds.length) {
      return;
    }
    this.dynamicContentEntranceService.find(0, 100, { _id: { $in: this.composition.dynamicContentIds } }).then(result => {
      this.dynamicContentEntrances = result.data;
      this.assignedContentEntranceService.db = this.dynamicContentEntrances;
      this.dynamicContentTable.refresh();
    });
  }
}
